import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { isTokenValid, startTokenRefresh, stopTokenRefresh } from './lib/axios';
import Users from './pages/Users';
import Profiles from './pages/Profiles';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Production from './pages/Production';
import Kanban from './pages/Kanban';
import Factories from './pages/Factories';
import ProcessMapping from './pages/ProcessMapping';
import RawMaterials from './pages/RawMaterials';
import Produtos from './pages/Produtos';
import AIInsights from './pages/AIInsights';
import VesselModels from './pages/VesselModels';
import Fornecedores from './pages/Fornecedores';
import EspecificacaoEmbarcacao from './pages/EspecificacaoEmbarcacao';
import EspecificacaoPDF from './pages/EspecificacaoPDF';
import Cronograma from './pages/Cronograma';
import BackupPage from './pages/BackupPage';
import Compras from './pages/Compras';
import SupplierScoreConfig from './pages/SupplierScoreConfig';
import ConfigAlertas from './pages/ConfigAlertas';
import Empresas from './pages/Empresas';
import LogsPage from './pages/LogsPage';
import CrmClientes from './pages/CrmClientes';
import CrmPipeline from './pages/CrmPipeline';
import Propostas from './pages/Propostas';
import CrmTarefas from './pages/CrmTarefas';
import CrmDashboard from './pages/CrmDashboard';
import Relatorios from './pages/Relatorios';
import WhatsAppConfig from './pages/WhatsAppConfig';
import WhatsAppChat from './pages/WhatsAppChat';
import ConfigZapi from './pages/ConfigZapi';
import Chamados from './pages/Chamados';
import NauCopilot from './pages/NauCopilot';
import AuditDashboard from './pages/AuditDashboard';
import StatusPage from './pages/StatusPage';
import SLADashboard from './pages/SLADashboard';
import Layout from './components/Layout';
import './App.css';

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('nautilus_token');
  const valid = isTokenValid();
  
  // Se não tem token ou token inválido, redireciona para login
  if (!token || !valid) {
    // Limpar dados antigos se token inválido
    if (token && !valid) {
      localStorage.removeItem('nautilus_token');
      localStorage.removeItem('nautilus_user');
    }
    return <Navigate to="/login" />;
  }
  
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('nautilus_token') && isTokenValid());

  // Verificar token periodicamente e auto-refresh (APENAS quando autenticado)
  useEffect(() => {
    if (!isAuthenticated) return;

    startTokenRefresh();

    const checkToken = () => {
      if (!isTokenValid()) {
        stopTokenRefresh();
        localStorage.removeItem('nautilus_token');
        localStorage.removeItem('nautilus_user');
        setIsAuthenticated(false);
      }
    };

    const interval = setInterval(checkToken, 60000);
    const handleFocus = () => checkToken();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      stopTokenRefresh();
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated]);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/" /> : <Login setIsAuthenticated={setIsAuthenticated} />
          } />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Layout setIsAuthenticated={setIsAuthenticated}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/kanban" element={<Kanban />} />
                    <Route path="/production" element={<Production />} />
                    <Route path="/factories" element={<Factories />} />
                    <Route path="/processes" element={<ProcessMapping />} />
                    <Route path="/materials" element={<RawMaterials />} />
                    <Route path="/produtos" element={<Produtos />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/profiles" element={<Profiles />} />
                    <Route path="/insights" element={<AIInsights />} />
                    <Route path="/vessel-models" element={<VesselModels />} />
                    <Route path="/fornecedores" element={<Fornecedores />} />
                    <Route path="/compras" element={<Compras />} />
                    <Route path="/configuracoes/supplier-score" element={<SupplierScoreConfig />} />
                    <Route path="/configuracoes/alertas" element={<ConfigAlertas />} />
                    <Route path="/empresas" element={<Empresas />} />
                    <Route path="/projects/:projectId/embarcacao/:embarcacaoIndex" element={<EspecificacaoEmbarcacao />} />
                    <Route path="/projects/:projectId/embarcacao/:embarcacaoIndex/pdf" element={<EspecificacaoPDF />} />
                    <Route path="/cronograma" element={<Cronograma />} />
                    <Route path="/backup" element={<BackupPage />} />
                    <Route path="/logs" element={<LogsPage />} />
                    <Route path="/crm/clientes" element={<CrmClientes />} />
                    <Route path="/crm/dashboard" element={<CrmDashboard />} />
                    <Route path="/crm/pipeline" element={<CrmPipeline />} />
                    <Route path="/crm/propostas" element={<Propostas />} />
                    <Route path="/crm/tarefas" element={<CrmTarefas />} />
                    <Route path="/relatorios" element={<Relatorios />} />
                    <Route path="/whatsapp" element={<WhatsAppChat />} />
                    <Route path="/configuracoes/whatsapp" element={<WhatsAppConfig />} />
                    <Route path="/configuracoes/zapi" element={<ConfigZapi />} />
                    <Route path="/configuracoes/chamados" element={<Chamados />} />
                    <Route path="/nau" element={<NauCopilot />} />
                    <Route path="/configuracoes/auditoria" element={<AuditDashboard />} />
                    <Route path="/configuracoes/status" element={<StatusPage />} />
                    <Route path="/configuracoes/sla" element={<SLADashboard />} />
                  </Routes>
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
